import { observer }             from 'mobx-react-lite';
import View                     from '@widesk-ui/components/View';
import Button                   from '@widesk-ui/components/Button';
import { HolderOutlined }       from '@ant-design/icons';
import { DeleteOutlined }       from '@ant-design/icons';
import { DndContext }           from '@dnd-kit/core';
import { PointerSensor }        from '@dnd-kit/core';
import { KeyboardSensor }       from '@dnd-kit/core';
import { useSensor }            from '@dnd-kit/core';
import { useSensors }           from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext }      from '@dnd-kit/sortable';
import { useSortable }          from '@dnd-kit/sortable';
import { arrayMove }            from '@dnd-kit/sortable';
import { verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS }                  from '@dnd-kit/utilities';
import FieldModel               from '@models/map/FieldModel';
import useDesignTokens          from '@/hooks/useDesignTokens';
import EditableText              from '@widesk-ui/components/form/EditableText';
import ButtonActions             from '@widesk-ui/components/ButtonActions';
import { EditOutlined }          from '@ant-design/icons';
import { FormModal }             from '@widesk-ui/components/modal/FormModal';
import FieldReferenceForm        from '@/forms/services/map/FieldReferenceForm';
import { NATURES }              from '@/forms/services/map/fieldNatures';

/**
 * Les champs qui composent la fiche d'un type, réordonnables au glisser-déposer.
 *
 * Écrit à la main plutôt qu'avec `FormList` : celui-ci impose des flèches monter/descendre et une
 * ligne par `FormItem`, là où cet écran a besoin de lire d'un coup d'œil la fiche produite — un nom,
 * une nature, un ordre. La valeur ne transite donc plus par le formulaire antd : le parent en garde
 * le tableau et le lit au moment d'enregistrer, ce qui est aussi plus simple à suivre.
 *
 * `@dnd-kit` est déjà la brique de réordonnancement du projet (voir `PositionTableContent`).
 */
const PlaceTypeFieldList = observer((props: {
	fieldUrns: Urn[];
	resolveField: (urn?: Urn) => FieldModel | undefined;
	onChange: (fieldUrns: Urn[]) => void;
	/** Enveloppe d'enregistrement du panneau, pour que le renommage d'un champ affiche le même
	 * "Enregistré" que les autres gestes. */
	onSave?: (task: () => Promise<void>) => Promise<void>;
}) => {
	const { fieldUrns, onChange } = props;

	// Aucune contrainte de distance : le geste ne part que depuis la poignée, il n'y a donc pas de
	// clic accidentel à filtrer. En imposer une se sentait comme une résistance, puis la ligne
	// rattrapait le curseur d'un coup au franchissement du seuil. Le clavier est géré aussi, la
	// poignée étant focalisable.
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	// Rien à rendre quand la liste est vide : c'est l'invitation à ajouter un champ qui tient lieu
	// d'état vide, dans `PlaceTypeForm`. Un message ici l'aurait précédée d'un constat, et l'écran
	// empilait trois blocs pour ne rien dire.
	if (!fieldUrns.length) return null;

	return (
		<DndContext
			sensors={sensors}
			// `restrictToParentElement` en plus de l'axe vertical : sans lui, la ligne suit le curseur
			// au-delà de la liste et s'échappe du panneau, alors qu'elle ne peut de toute façon être
			// déposée qu'entre ses voisines.
			modifiers={[restrictToVerticalAxis, restrictToParentElement]}
			onDragEnd={({ active, over }) => {
				if (!over || active.id === over.id) return;

				const from = fieldUrns.indexOf(active.id as Urn);
				const to = fieldUrns.indexOf(over.id as Urn);

				if (from < 0 || to < 0) return;

				onChange(arrayMove(fieldUrns, from, to));
			}}
		>
			<SortableContext items={fieldUrns} strategy={verticalListSortingStrategy}>
				<View gap="xs">
					{fieldUrns.map(urn => (
						<FieldRow
							key={urn}
							urn={urn}
							model={props.resolveField(urn)}
							onSave={props.onSave}
							onRemove={() => onChange(fieldUrns.filter(u => u !== urn))}
						/>
					))}
				</View>
			</SortableContext>
		</DndContext>
	);
});

const FieldRow = observer((props: {
	urn: Urn;
	model?: FieldModel;
	onRemove: () => void;
	onSave?: (task: () => Promise<void>) => Promise<void>;
}) => {
	const tokens = useDesignTokens();
	const { model } = props;

	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: props.urn });

	const nature = model?.fieldType?.reference
		? NATURES.find(n => n.reference === model.fieldType.reference)
		: undefined;

	return (
		<View
			ref={setNodeRef}
			row
			centerV
			gap="sm"
			p="xs"
			br={8}
			bg={tokens.surface.card}
			{...attributes}
			style={{
				border: `1px solid ${isDragging ? tokens.color.primary : tokens.border.default}`,
				transform: CSS.Translate.toString(transform),
				transition,
				// Déplacement composé par le GPU : sans ça, le navigateur repeint la ligne à chaque
				// image et le glisser accroche.
				willChange: isDragging ? 'transform' : undefined,
				// Au-dessus des autres lignes pendant le déplacement, sinon elle passe dessous.
				...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
			}}
		>
			{/* `touchAction: none` : sans lui, le navigateur interprète le geste comme un défilement et
			    le glisser ne démarre pas du tout sur écran tactile. */}
			<div ref={setActivatorNodeRef} {...listeners} style={{ cursor: 'grab', display: 'flex', touchAction: 'none' }}>
				<Button type="text" size="small" icon={<HolderOutlined />} style={{ cursor: 'grab' }} />
			</div>

			<View
				w={28}
				h={28}
				br={7}
				center
				color={tokens.color.primary}
				bg={tokens.fill.subtle}
				style={{ flexShrink: 0 }}
			>
				{nature?.icon}
			</View>

			<View flex gap={3} style={{ minWidth: 0 }}>
				{/* Renommable en place : c'est le seul endroit où l'on voit le champ dans son contexte, et
				    ouvrir un formulaire pour changer un mot était disproportionné. Le gras est porté par
				    l'enveloppe et non par l'input : celui d'`EditableText` hérite de la typographie
				    environnante, il la suit donc aussi en mode saisie.

				    Le glisser ne partant que de la poignée, cliquer ici n'en déclenche aucun. */}
				<View bold style={{ minWidth: 0 }}>
					{model ? (
						<EditableText
							value={model.label}
							size="small"
							placeholder="Nom du champ"
							errorMessage="Impossible de renommer ce champ"
							onSubmit={async label => {
								if (!label) throw new Error('Le nom est obligatoire.');

								const task = async () => void await model.patch({ label });

								await (props.onSave ? props.onSave(task) : task());
							}}
						/>
					) : (
						<span style={{ color: tokens.text.muted }}>Champ introuvable</span>
					)}
				</View>

				<View size="small" color={tokens.text.muted}>{nature?.label}</View>
			</View>

			<ButtonActions
				type="text"
				size="small"
				style={{ width: 26, height: 26, padding: 0 }}
				actions={[
					{
						icon: <EditOutlined />,
						label: 'Modifier la référence',
						disabled: !model,
						onClick: () => model && FormModal.open({
							// Le titre reprend l'action : la modale ne modifie QUE la référence, alors qu'un
							// "Modifier le champ …" laissait attendre le formulaire complet.
							title: `Modifier la référence de "${model.label}"`,
							// Un seul champ de saisie court : la largeur par défaut donnait une modale
							// disproportionnée par rapport à ce qu'elle demande.
							width: 420,
							children: <FieldReferenceForm model={model} />,
						}),
					},
					{
						// "Retirer de ce type" et non "Supprimer" : le champ appartient au projet et reste
						// disponible pour les autres types. Seul le lien est détruit.
						icon: <DeleteOutlined />,
						label: 'Retirer de ce type',
						danger: true,
						onClick: props.onRemove,
					},
				]}
			/>
		</View>
	);
});

export default PlaceTypeFieldList;
